import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Button,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import { useState } from "react";
import { Inventory } from "types/inventory";

export const InventoryDeviceWipeChip = ({ inventory, ...props }: { inventory?: Inventory } & ChipProps) => {
  const state = inventory?.drive1ErasureStatus?.toLowerCase() ?? "";

  switch (state) {
    case "passed":
      return (
        <Tooltip arrow disableInteractive title="Data was successfully erased.">
          <Chip color="success" icon={<CheckCircleIcon />} label="Wipe Passed" variant="outlined" {...props} />
        </Tooltip>
      );
    case "failed":
      return (
        <Tooltip
          arrow
          disableInteractive
          title="A disk wipe was attempted, but did not successfully pass. There may still be sensitive information on this device."
        >
          <Chip color="error" icon={<CancelIcon />} label="Wipe Failed" variant="outlined" {...props} />
        </Tooltip>
      );
    case "manual":
      return (
        <Tooltip arrow disableInteractive title="Device was manually wiped.">
          <Chip color="success" icon={<CheckCircleIcon />} label="Manually Wiped" variant="outlined" {...props} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip arrow disableInteractive title="Device has not been wiped yet.">
          <Chip color="warning" icon={<ErrorIcon />} label="Not Wiped" variant="outlined" {...props} />
        </Tooltip>
      );
  }
};
export const DeviceWipeStatus = () => {
  const { setFieldValue, values } = useFormikContext<{ inventory: Inventory }>();
  const state = values.inventory.drive1ErasureStatus?.toLowerCase() ?? "";
  const clickable = !["passed", "manual"].includes(state);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(clickable);
  const handleClose = () => setOpen(false);

  const DiskWipeDialog = (dialogProps: DialogProps) => {
    const handleConfirm = () => {
      setFieldValue("inventory.drive1ErasureStatus", "Manual");
      handleClose();
    };

    return (
      <Dialog {...dialogProps}>
        <DialogTitle>Set wipe status to Manually Wiped</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to set the wipe status to Manually Wiped? This should only be done for devices that
            cannot be programmatically erased.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" onClick={handleConfirm}>
            Set to Manually Wiped
          </Button>
          <Button size="small" variant="contained" onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <InventoryDeviceWipeChip inventory={values.inventory} clickable={clickable} onClick={handleOpen} />
      <DiskWipeDialog open={open} onClose={handleClose} />
    </>
  );
};
